import { Injectable } from '@angular/core';
import { AD_OPT_OUT_INTEGRATIONS, buildAdConsentContext } from '@ecom/common';
import { MetaTrackingService } from '@core-mkt/services/meta/meta-tracking.service';
import { AnalyticsConsentService } from '../analytics/analytics-consent.service';
import { AnalyticsInitializerService } from '../analytics/analytics-initializer.service';

/** The Analytics 2.0 methods this service forwards to; each returns a promise of the dispatched context. */
type SegmentAnalytics = Record<
  'track' | 'page' | 'identify' | 'group' | 'alias',
  (...args: unknown[]) => Promise<unknown>
>;

@Injectable({
  providedIn: 'root',
})
export class SegmentService {
  constructor(
    private readonly metaTrackingService: MetaTrackingService,
    private readonly analyticsInitializer: AnalyticsInitializerService,
    private readonly consentService: AnalyticsConsentService,
  ) {}

  /**
   * Calls a Segment Analytics method
   * @param method Analytics method to be used (e.g., 'track', 'page', 'identify')
   * @param trackName Name of the action/object being tracked
   * @param params Extra data for the tracked method
   * @param options Optional context/options for the analytics call (e.g., consent settings)
   * @returns For `track`, Analytics.js's dispatch promise, so a caller can wait for delivery before
   *   unloading the page; nothing for other methods.
   */
  callAnalyticsMethod(method: string, trackName: string, params?: object, options?: object): Promise<unknown> | void {
    const analytics = this.analyticsInitializer.analytics as SegmentAnalytics | null;

    if (!analytics) {
      // eslint-disable-next-line no-console -- no logger util in core-mkt services
      console.warn('[Segment Service] Analytics instance is null, cannot call method');
      return;
    }

    const metaContext = this.metaTrackingService.getMetaContext();
    const finalParams = metaContext ? { ...params, ...metaContext } : params;
    options = this.withOptOut(options);

    // Use Analytics 2.0 API methods
    switch (method) {
      case 'track':
        return analytics.track(trackName, finalParams, options);
      case 'page':
        void analytics.page(trackName, finalParams, options);
        break;
      case 'identify':
        void analytics.identify(trackName, finalParams, options);
        break;
      case 'group':
        void analytics.group(trackName, finalParams, options);
        break;
      case 'alias': {
        const previousId = (params as { previousId?: string })?.previousId;
        // Merge meta context into options.context instead of params
        const aliasOptions = metaContext
          ? {
              ...options,
              context: { ...(((options as Record<string, unknown>)?.context as object) || {}), ...metaContext },
            }
          : options;
        void analytics.alias(trackName, previousId, aliasOptions);
        break;
      }
      default: {
        // Fallback for other methods using type assertion
        const analyticsAny = analytics as unknown as Record<string, (...args: unknown[]) => void>;
        if (typeof analyticsAny[method] === 'function') {
          analyticsAny[method](trackName, finalParams, options);
        } else {
          // eslint-disable-next-line no-console -- no logger util in core-mkt services
          console.warn('[Segment Service] Method not found:', method);
        }
        break;
      }
    }
  }

  /**
   * For opted-out visitors, switches every ad destination off for this event and forces the
   * consent context to DataSharing false, overriding any caller-supplied consent: an event must never
   * report sharing as allowed while the visitor is still opted out (e.g. GPC on and "allow" saved).
   */
  private withOptOut(options?: object): object | undefined {
    if (!this.consentService.isOptedOut()) {
      return options;
    }

    const callerIntegrations = (options as { integrations?: object } | undefined)?.integrations;
    return {
      ...options,
      ...buildAdConsentContext(true),
      integrations: { ...callerIntegrations, ...AD_OPT_OUT_INTEGRATIONS },
    };
  }
}
